import type { ReactNode } from 'react';
import {
  DEFAULT_CONTENT_LINKS,
  MAIN_NAVIGATION,
  type WebsiteContentLinks,
} from '../../utils/websiteSsr/content';
import { getContentBook } from '../content';
import TransitionLink from '../client/TransitionLink';

export default function SiteFooter({
  text,
  imageUrl,
  language = 'en',
  links = DEFAULT_CONTENT_LINKS,
}: {
  text: string;
  imageUrl: string | null;
  imageAlt?: string;
  language?: string;
  links?: WebsiteContentLinks;
}): ReactNode {
  const book = getContentBook(language);
  return (
    <footer className="mt-6 border-t border-border px-[var(--web-side-padding)] pb-8 pt-10">
      <div className="mx-auto max-w-web">
        <div className="grid gap-8 md:grid-cols-[2fr_1fr]">
          <nav aria-label={book.nav.menu}>
            <ul className="m-0 grid list-none grid-cols-2 gap-x-6 gap-y-4 p-0 sm:grid-cols-3">
              {[...MAIN_NAVIGATION, { key: 'gifts' as const, path: '/gifts' }].map((item) => (
                <li key={item.key}>
                  <TransitionLink
                    to={item.path}
                    className="text-sm font-medium text-text-secondary underline-offset-4 hover:text-accent hover:underline"
                  >
                    {book.nav[item.key]}
                  </TransitionLink>
                </li>
              ))}
            </ul>
          </nav>
          <div>
            <p className="m-0 text-lg font-semibold">Georgian Tea Portal</p>
            <p className="mt-2 text-sm text-text-secondary">From leaf to cup.</p>
            <a
              href={`tel:${links.phone}`}
              className="text-sm text-accent underline underline-offset-4"
            >
              {links.phone} (Maria)
            </a>
            {links.email && (
              <a
                href={`mailto:${links.email}`}
                className="mt-3 block break-words text-sm text-accent underline underline-offset-4"
              >
                {links.email}
              </a>
            )}
            <div className="mt-4 flex flex-wrap gap-5">
              {links.telegram && (
                <a className="text-sm text-accent hover:underline" href={links.telegram}>
                  Telegram
                </a>
              )}
              {links.instagram && (
                <a className="text-sm text-accent hover:underline" href={links.instagram}>
                  Instagram
                </a>
              )}
            </div>
          </div>
        </div>
        {(text || imageUrl) && (
          <div
            className={`relative isolate mt-8 flex min-h-[150px] items-end overflow-hidden rounded-3xl ${imageUrl ? 'bg-scrim' : 'bg-secondary'}`}
          >
            {imageUrl && (
              <>
                <img
                  src={imageUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_5%,var(--color-hero-overlay-bottom)_100%)]" />
              </>
            )}
            {text && (
              <p
                className={`relative z-[1] m-0 max-w-[65ch] p-6 text-base font-medium leading-relaxed md:px-8 ${imageUrl ? 'text-on-scrim' : 'text-text'}`}
              >
                {text}
              </p>
            )}
          </div>
        )}
      </div>
    </footer>
  );
}
